import { useCallback, useEffect, useState } from "react";
import { api, fmtUSD, fmtPrice, fmtDate } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function TradesTable({ rows, closed }) {
  return (
    <div className="border border-subtle bg-card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-surface-hover border-subtle">
            <TableHead>Instrument</TableHead>
            <TableHead>Side</TableHead>
            <TableHead className="text-right">Lots</TableHead>
            <TableHead className="text-right">Entry</TableHead>
            <TableHead className="text-right">{closed ? "Close" : "Current"}</TableHead>
            <TableHead className="text-right">SL / TP</TableHead>
            <TableHead className="text-right">P&amp;L</TableHead>
            <TableHead>Opened</TableHead>
            {closed && <TableHead>Closed</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={closed ? 9 : 8} className="text-center py-12 text-muted-foreground">No trades</TableCell></TableRow>
          )}
          {rows.map((t) => (
            <TableRow key={t.id} className="border-subtle hover:bg-surface-hover" data-testid={`client-trade-${t.id}`}>
              <TableCell className="font-semibold">{t.instrument}</TableCell>
              <TableCell>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.side === "buy" ? "bg-profit/15 text-profit" : "bg-loss/15 text-loss"}`}>
                  {t.side.toUpperCase()}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono-num">{t.lots.toFixed(2)}</TableCell>
              <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, t.entry_price)}</TableCell>
              <TableCell className="text-right font-mono-num">{fmtPrice(t.instrument, closed ? t.close_price : t.current_price)}</TableCell>
              <TableCell className="text-right font-mono-num text-xs text-muted-foreground">
                {t.stop_loss ? fmtPrice(t.instrument, t.stop_loss) : "—"} / {t.take_profit ? fmtPrice(t.instrument, t.take_profit) : "—"}
              </TableCell>
              <TableCell className={`text-right font-mono-num font-semibold ${t.pnl >= 0 ? "text-profit" : "text-loss"}`} data-testid={`client-pnl-${t.id}`}>
                {fmtUSD(t.pnl, { signed: true })}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDate(t.opened_at)}</TableCell>
              {closed && <TableCell className="text-xs text-muted-foreground">{fmtDate(t.closed_at)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ClientTrades() {
  const [open, setOpen] = useState([]);
  const [closed, setClosed] = useState([]);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/client/trades", { params: { status: "open" } }),
      api.get("/client/trades", { params: { status: "closed" } }),
    ]);
    setOpen(a.data);
    setClosed(b.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="client-trades-page">
      <Tabs defaultValue="open">
        <TabsList className="bg-card border border-subtle">
          <TabsTrigger value="open" data-testid="ct-tab-open">Open ({open.length})</TabsTrigger>
          <TabsTrigger value="closed" data-testid="ct-tab-closed">Closed ({closed.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="open" className="mt-4">
          <TradesTable rows={open} closed={false} />
        </TabsContent>
        <TabsContent value="closed" className="mt-4">
          <TradesTable rows={closed} closed />
        </TabsContent>
      </Tabs>
    </div>
  );
}
